import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type EntitySuggestion } from '@bliks-io/contracts';
import { toSuccessorUpdate } from '@bliks-io/ui';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import type { SuccessorChooserKey } from '@/lib/successorEntityMapping';
import type { SuccessorUpdateData } from '../../../pages/processes/recording/types';
import { toEntityOrSuggestionRef } from '../shared/pendingSuggestionUtils';
import type { SuggestionPickEvent } from '../shared/suggestionPickEvent';
import { TypePickerModalShell } from '../type-picker/TypePickerModalShell';
import { useBodyRefsByType, useTentativeByType } from '../type-picker/useTypePickerState';
import { SuccessorTypeSidebar } from './SuccessorTypeSidebar';
import { SuccessorPickerPhaseContent } from './SuccessorPickerPhaseContent';
import { useSuccessorPickerConfigs } from './useSuccessorPickerConfigs';

type EntityType = Exclude<SuccessorChooserKey, 'unclear' | 'none'>;

const SUCCESSOR_TYPE_KEYS = ['person', 'team', 'role', 'system', 'externalContact'] as const;

function isEntityType(v: SuccessorChooserKey | null): v is EntityType {
  return v !== null && v !== 'unclear' && v !== 'none';
}

export type SuccessorPickerMode = 'successor' | 'assignee' | 'starter' | 'reassign';

export interface SuccessorEntityPickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (update: SuccessorUpdateData) => void;
  processId?: string;
  recordingId?: string;
  /**
   * Start event node behind the starter picker (issue 11): suggestions
   * created from here anchor to this node (`sourceRecordingId`).
   */
  startEventNodeId?: string;
  /** Draft-editor host: create/link post to the draft suggestion routes. */
  draftProcessId?: string;
  sourceRuleId?: string;
  mode?: SuccessorPickerMode;
  /** Overrides the assignee/starter default for the "unclear" row. */
  hideUnclear?: boolean;
  /** Overrides the assignee/starter default for the "none/ending" row. */
  hideNone?: boolean;
  /** Overrides the assignee/starter default for the "external contact" row. */
  hideExternalContact?: boolean;
  previousStepName?: string;
  nextStepNumber?: number;
  excludeIds?: {
    persons: string[];
    orgFunctions: string[];
    systems: string[];
    externalContacts: string[];
  };
  /**
   * Pending suggestions on the source recording; filtered by kind inside the
   * OrgFunction / System picker hooks to render their ghost section.
   */
  pendingSuggestions?: EntitySuggestion[];
  /**
   * Notification that the suggest-new dialog persisted (`'created'`) or
   * adopted (`'adopted'`) a suggestion — the slot ref still arrives via
   * `onSave`. Reassign flows narrow on `kind` to track creates for the
   * guarded withdraw on cancel and seed their proposed-name lookups.
   */
  onSuggestionCreated?: (event: SuggestionPickEvent) => void;
  /**
   * Editor opt-in: create Teams / Funktionen / Systems directly instead of
   * suggesting them.
   */
  allowCreate?: boolean;
}

const EMPTY_IDS: string[] = [];

const EMPTY_EXCLUDE_IDS = {
  persons: EMPTY_IDS,
  orgFunctions: EMPTY_IDS,
  systems: EMPTY_IDS,
  externalContacts: EMPTY_IDS
};

const SUCCESSOR_MODAL_SIZES = {
  compact: { width: 600, minHeight: 640 },
  expanded: { width: 1120, minHeight: 800 }
} as const;

function resolveEffectiveHides(props: SuccessorEntityPickerModalProps) {
  const mode: SuccessorPickerMode = props.mode ?? 'successor';
  // Positive list: only these modes get assignee/starter bucket scoping. A new
  // mode is successor-shaped by default (e.g. `reassign`, which overrides only the
  // header copy and passes its own hide flags explicitly) unless added here.
  const isAssigneeLike = mode === 'assignee' || mode === 'starter';
  return {
    mode,
    // Picker hooks still scope behaviour to the assignee/starter shape via this flag.
    isAssigneeMode: isAssigneeLike,
    effectiveHideUnclear: props.hideUnclear ?? isAssigneeLike,
    effectiveHideNone: props.hideNone ?? isAssigneeLike,
    effectiveHideExternalContact: props.hideExternalContact ?? isAssigneeLike
  };
}

function buildEntityPayload(type: EntityType, id: string): SuccessorUpdateData {
  switch (type) {
    case 'person':
      return { persons: [id], successorStatus: '' };
    // Team and role are presentation phases over ONE stored slot — both kinds
    // save into the same `orgFunctions` union-ref array (ADR 0004).
    case 'team':
    case 'role':
      return { orgFunctions: [toEntityOrSuggestionRef(id)], successorStatus: '' };
    case 'system':
      return { systems: [toEntityOrSuggestionRef(id)], successorStatus: '' };
    case 'externalContact':
      return { externalContacts: [id], successorStatus: '' };
  }
}

export function SuccessorEntityPickerModal(props: SuccessorEntityPickerModalProps) {
  const {
    open,
    onOpenChange,
    onSave,
    processId,
    recordingId,
    startEventNodeId,
    draftProcessId,
    sourceRuleId,
    previousStepName,
    nextStepNumber,
    excludeIds = EMPTY_EXCLUDE_IDS,
    pendingSuggestions,
    allowCreate
  } = props;
  const {
    mode,
    isAssigneeMode,
    effectiveHideUnclear,
    effectiveHideNone,
    effectiveHideExternalContact
  } = resolveEffectiveHides(props);
  const { t } = useTranslation();
  const [currentType, setCurrentType] = useState<SuccessorChooserKey | null>(null);
  const { selectedIdsFor, updateTentative, resetTentative } = useTentativeByType<EntityType>();

  // Mirrors the active picker's bucket↔all viewMode so the footer can render
  // a "back to suggestions" affordance.
  const [pickerViewMode, setPickerViewMode] = useState<'bucket' | 'all'>('bucket');

  const pickerConfigs = useSuccessorPickerConfigs({
    open,
    currentType,
    processId,
    recordingId,
    startEventNodeId,
    draftProcessId,
    sourceRuleId,
    isAssigneeMode,
    pendingSuggestions,
    allowCreate
  });

  const handleSelectType = useCallback(
    (next: SuccessorChooserKey | null) => {
      setCurrentType(next);
      setPickerViewMode('bucket');
      // A fresh phase always lands in bucket view, where the department rail
      // (an 'all'-view affordance) isn't shown — so a carried-over filter would
      // be invisible and unresettable. Clear it on every phase change, mirroring
      // handleBackToBuckets. System/external pickers have no department filter.
      pickerConfigs.orgFunctionHook.config.departmentFilter?.onChange(null);
      pickerConfigs.personHook.config.departmentFilter?.onChange(null);
    },
    [pickerConfigs]
  );

  const bodyRefs = useBodyRefsByType(SUCCESSOR_TYPE_KEYS);
  const activeBodyRef = isEntityType(currentType) ? bodyRefs[currentType] : null;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      handleSelectType(null);
      resetTentative();
    }
    onOpenChange(next);
  };

  const tentativeForCurrent = isEntityType(currentType) ? selectedIdsFor(currentType) : EMPTY_IDS;

  const closeAndReset = () => {
    handleOpenChange(false);
  };

  const handleSave = () => {
    if (!currentType) return;
    if (currentType === 'unclear') {
      onSave(toSuccessorUpdate({ type: 'unclear' }));
      closeAndReset();
      return;
    }
    if (currentType === 'none') {
      onSave(toSuccessorUpdate({ type: 'none' }));
      closeAndReset();
      return;
    }
    const id = tentativeForCurrent[0];
    if (!id) return;
    onSave(buildEntityPayload(currentType, id));
    closeAndReset();
  };

  const handleInlineSave = (update: SuccessorUpdateData) => {
    onSave(update);
    closeAndReset();
  };

  const handleExternalContactCreated = (id: string) => {
    updateTentative('externalContact', [id]);
  };

  const isTerminal = currentType === 'unclear' || currentType === 'none';
  const hasTentativeSelection = isEntityType(currentType) && tentativeForCurrent.length > 0;

  const saveEnabled = isTerminal || hasTentativeSelection;
  const saveLabel = getSaveLabel(currentType, t);
  const statusText = saveEnabled
    ? t('entityPicker.successor.statusReadyToSave')
    : t('entityPicker.successor.statusPickToContinue');
  const title = t(`entityPicker.${mode}.title`);
  const subtitle =
    currentType === null
      ? t(`entityPicker.${mode}.descriptionCollapsed`)
      : t(`entityPicker.${mode}.descriptionExpanded`);
  const eyebrow = buildEyebrow(t, previousStepName, nextStepNumber);
  const expanded = currentType !== null;

  return (
    <TypePickerModalShell
      open={open}
      onOpenChange={handleOpenChange}
      sizes={SUCCESSOR_MODAL_SIZES}
      size={expanded ? 'expanded' : 'compact'}
      headerIcon={<ENTITY_ICONS.successor className="h-5 w-5" />}
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      sidebar={
        expanded ? (
          <SuccessorTypeSidebar
            currentType={currentType!}
            onSelect={handleSelectType}
            hideExternalContact={effectiveHideExternalContact}
            hideNone={effectiveHideNone}
            hideUnclear={effectiveHideUnclear}
          />
        ) : undefined
      }
      resizableAutoSaveId="successor-picker-sidebar"
      phaseKey={currentType ?? 'chooser'}
      footerHint={statusText}
      onBackToBuckets={
        pickerViewMode === 'all' && activeBodyRef?.current?.canReturnToBuckets()
          ? () => activeBodyRef.current?.backToBuckets()
          : undefined
      }
      primary={
        currentType !== null
          ? {
              label: saveLabel,
              onClick: handleSave,
              disabled: !saveEnabled
            }
          : undefined
      }
    >
      <SuccessorPickerPhaseContent
        currentType={currentType}
        pickerConfigs={pickerConfigs}
        bodyRefs={bodyRefs}
        excludeIds={excludeIds}
        computeSelectedIds={selectedIdsFor}
        onUpdateTentative={updateTentative}
        onViewModeChange={setPickerViewMode}
        onSelectType={handleSelectType}
        onExternalContactCreated={handleExternalContactCreated}
        onInlineSave={handleInlineSave}
        onSuggestionCreated={props.onSuggestionCreated}
        effectiveHideExternalContact={effectiveHideExternalContact}
        effectiveHideNone={effectiveHideNone}
        effectiveHideUnclear={effectiveHideUnclear}
        processId={processId}
      />
    </TypePickerModalShell>
  );
}

function getSaveLabel(currentType: SuccessorChooserKey | null, t: (key: string) => string): string {
  if (currentType === 'unclear') return t('entityPicker.successor.unclear.ctaConfirm');
  if (currentType === 'none') return t('entityPicker.successor.none.ctaConfirm');
  return t('entityPicker.successor.ctaConfirmEntity');
}

function buildEyebrow(
  t: (key: string, opts?: Record<string, unknown>) => string,
  previousStepName: string | undefined,
  nextStepNumber: number | undefined
): string | undefined {
  if (!previousStepName || nextStepNumber === undefined) return undefined;
  return t('entityPicker.successor.breadcrumbAfter', {
    stepName: previousStepName,
    stepNumber: nextStepNumber
  });
}
